import { createFileRoute, Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mountain, Bike, Users, BarChart3, ArrowLeft, Star, Shield, Tag, BadgeCheck } from "lucide-react";
import { useIsAdmin } from "@/lib/useIsAdmin";
import { supabase } from "@/integrations/supabase/client";
import { WattsLogo } from "@/components/WattsLogo";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

const NAV = [
  { to: "/admin/puertos", label: "Puertos", Icon: Mountain },
  { to: "/admin/rides",  label: "Salidas", Icon: Bike },
  { to: "/admin/users",  label: "Usuarios", Icon: Users },
  { to: "/admin/embajadores", label: "Embajadores", Icon: Star },
  { to: "/admin/nicknames", label: "Nicknames", Icon: Tag },
  { to: "/admin/clubs",  label: "Clubs", Icon: Shield },
  { to: "/admin/verifications", label: "Verificaciones", Icon: BadgeCheck },
  { to: "/admin/stats",  label: "Stats", Icon: BarChart3 },
] as const;

function AdminLayout() {
  const { isAdmin, loading } = useIsAdmin();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mfaChecked, setMfaChecked] = useState(false);

  const isMfaRoute = pathname === "/admin/setup-mfa" || pathname === "/admin/verify-mfa";

  useEffect(() => {
    if (!loading && isAdmin === false) navigate({ to: "/", replace: true });
  }, [loading, isAdmin, navigate]);

  useEffect(() => {
    if (loading || !isAdmin) return;
    if (isMfaRoute) { setMfaChecked(true); return; }
    (async () => {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const hasVerified = (factors?.totp ?? []).some((f) => f.status === "verified");
      if (!hasVerified) { navigate({ to: "/admin/setup-mfa", replace: true }); return; }
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel !== "aal2") { navigate({ to: "/admin/verify-mfa", replace: true }); return; }
      setMfaChecked(true);
    })();
  }, [loading, isAdmin, pathname, isMfaRoute, navigate]);

  useEffect(() => {
    if (!loading && isAdmin && mfaChecked && pathname === "/admin") {
      navigate({ to: "/admin/puertos", replace: true });
    }
  }, [loading, isAdmin, mfaChecked, pathname, navigate]);

  if (loading || (isAdmin && !mfaChecked && !isMfaRoute)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-pulse text-sm text-muted-foreground">Cargando admin…</div>
      </div>
    );
  }
  if (!isAdmin) return null;
  if (isMfaRoute) return <Outlet />;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex w-56 shrink-0 flex-col border-r border-border bg-[#0f0f0f] p-4">
        <div className="mb-6 flex flex-col items-center gap-2">
          <WattsLogo width={100} />
          <span className="rounded-full bg-[#F5C518]/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#F5C518]">
            Admin
          </span>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, Icon }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                  active ? "bg-primary/15 text-primary font-semibold" : "text-white/70 hover:bg-white/5"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </Link>
            );
          })}
        </nav>
        <Link
          to="/map"
          className="mt-auto flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-white/50 hover:bg-white/5"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Volver a la app
        </Link>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed inset-x-0 top-0 z-40 border-b border-border bg-[#0f0f0f]/95 backdrop-blur-md">
        <div className="flex items-center justify-between px-3 pt-[max(env(safe-area-inset-top),0.5rem)] pb-2">
          <div className="flex items-center gap-2">
            <WattsLogo size={24} variant="icon" />
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">Admin</span>
          </div>
          <Link to="/map" className="text-xs text-white/60">Salir</Link>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-2 pb-2">
          {NAV.map(({ to, label, Icon }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs ${
                  active ? "bg-primary text-primary-foreground font-bold" : "bg-[#1a1a1a] text-white/70 border border-border"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </Link>
            );
          })}
        </nav>
      </div>

      <main className="flex-1 overflow-y-auto pt-[110px] md:pt-0 px-4 md:px-8 py-6 pb-16">
        <Outlet />
      </main>
    </div>
  );
}
